import MediaDeviceInfoWithState from "./MediaDeviceInfoPlus";
import {Config} from "../contexts/Configuration";
import {AudioInputHandler} from "../core/AudioInputHandler";

/**
 * MediaDevicesUtils - Singleton for managing audio input/output devices
 *
 * ## Disconnected Device Persistence
 *
 * This class maintains disconnected devices in the device list so users can see
 * which device was previously selected, even after it's unplugged. This is important
 * for user experience as it:
 * - Shows the user what device they had configured
 * - Allows automatic reconnection when the device is plugged back in
 * - Persists across page reloads and navigation
 *
 * ### How it works:
 *
 * 1. **Device caching**: When a device is selected, it's saved to localStorage
 *    via `rememberSelectedDevice()` / `rememberSelectedOutputDevice()` with:
 *    - `deviceId`, `label`, `groupId`, `kind`
 *    - `lastSeen`: timestamp of last connection
 *    - `preselected`: flag for auto-selection on load
 *    - `lastPanelId`: which panel it belongs to (for outputs)
 *
 * 2. **On enumerate** (`enumerateDevices()`):
 *    - Compares API devices with cached devices
 *    - If cached device NOT in API list → added to device list with `isActive = false`
 *    - If cached device IS in API list → marked as `isActive = true`
 *    - Devices disconnected for more than 10 days are automatically forgotten
 *
 * 3. **Change detection**: Broadcasts `mediadevices-changed` event with:
 *    - `newDevices`: devices appearing for the first time
 *    - `disconnectedDevices`: devices that went from active → inactive
 *    - `reconnectedDevices`: devices that went from inactive → active
 *
 * 4. **Visual feedback** (handled by DeviceSelector):
 *    - Inactive devices show "(inactive)" prefix
 *    - CSS class `device-selected-disconnected` for styling
 *
 * @see DeviceSelector - UI component that displays devices
 * @see MediaDeviceInfoWithState - device model with `isActive` flag
 */
class MediaDevicesUtils {

    private AVAILABLE_DEVICES_POLL_INTERVAL_TIME = 3000; // ms
    private static TEN_DAYS = 1000 * 60 * 60 * 24 * 10;

    private _initOnce: boolean;
    private _havePermissions: boolean;
    private inputDevices: MediaDeviceInfoWithState[] = [];
    private outputDevices: MediaDeviceInfoWithState[] = [];

    //InputStreamIntervenidos
    private intervenedInputStreams: { [k: string]: AudioInputHandler } = {};
    //deviceId,inputStreams[]


    private streamByDevices: { [k: string]: MediaStream } = {};
    private currentPermissions: string = "TODO"

    private static _instance: MediaDevicesUtils;
    private availableDevicesPollTimer: NodeJS.Timeout | null = null;
    private enumerateDevicesInFlight: Promise<boolean> | null = null;
    private enumerateDevicesQueued = false;

    private constructor() {
        this._initOnce = false;
        this._havePermissions = false;
    }

    public static instance(): MediaDevicesUtils {
        if (!MediaDevicesUtils._instance) {
            MediaDevicesUtils._instance = new MediaDevicesUtils();
        }
        return MediaDevicesUtils._instance;
    }

    public async init(): Promise<boolean> {
        if (this._initOnce) {
            return Promise.resolve(true);
        }

        this._initOnce = true;

        try {

            return this.enumerateDevices()
                .then(ok => {
                    console.debug("enumerateDevices ok? ", ok)

                    if (ok && this.supportsDeviceChangeEvent()) {
                        navigator.mediaDevices.addEventListener('devicechange', () => this.enumerateDevices());
                    } else {
                        console.log("[MediaDevices] Browser does not support automatic device change detection. Polling every", this.AVAILABLE_DEVICES_POLL_INTERVAL_TIME, " ms...")
                        if (!this.availableDevicesPollTimer) {
                            this.availableDevicesPollTimer = setInterval(
                                () => this.enumerateDevices(),
                                this.AVAILABLE_DEVICES_POLL_INTERVAL_TIME);
                        }
                    }
                    return ok;
                });
        } catch (e) {
            throw new Error("Unable to access devices.");
        }
    }

    public stop() {
        if (this.availableDevicesPollTimer) {
            clearInterval(this.availableDevicesPollTimer);
            this.availableDevicesPollTimer = null;
        }

        this.freeAllAudioInputHandlers()
    }

    public async promptPermissions() {
        return await navigator.mediaDevices.getUserMedia({audio: true, video: false})
            .then(stream => {
                stream.getTracks().forEach(track => track.stop());
                return true;
            })
    }

    public rememberSelectedDevice(mediaDevice: MediaDeviceInfoWithState, shouldUpdateLocalD: boolean) {
        mediaDevice.lastSeen = Date.now();
        mediaDevice.preselected = true;

        let cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

        cache = {...cache, inputDeviceConfig: mediaDevice};

        localStorage.setItem('preSelectedConfig', JSON.stringify(cache));

        if (shouldUpdateLocalD) {
            this.inputDevices = this.inputDevices.map(d => {
                if (d.deviceId === mediaDevice.deviceId) {
                    return mediaDevice;
                } else {
                    d.preselected = false;
                    return d;
                }
            })
        }
    }

    public rememberSelectedOutputDevice(mediaDevice: MediaDeviceInfoWithState, panelId: string, shouldUpdateLocalD: boolean) {
        mediaDevice.lastSeen = Date.now();
        mediaDevice.preselected = true;//preselectedDevice.preselected;
        mediaDevice.lastPanelId = panelId;

        let cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

        if (panelId.startsWith('bipanel_')) {
            const biPanelOutputDevices = cache.biPanelOutputDevicesConfig || {};
            biPanelOutputDevices[panelId] = mediaDevice;
            cache = {...cache, biPanelOutputDevicesConfig: biPanelOutputDevices};
        } else {
            const preSelectedOutputDevices = cache.outputDevicesConfig || {};
            preSelectedOutputDevices[panelId] = mediaDevice;
            cache = {...cache, outputDevicesConfig: preSelectedOutputDevices};
        }

        localStorage.setItem('preSelectedConfig', JSON.stringify(cache));

        if (shouldUpdateLocalD) {
            this.outputDevices = this.outputDevices.map(d => {
                if (d.deviceId === mediaDevice.deviceId) {
                    return mediaDevice;
                } else {
                    d.preselected = false;
                    return d;
                }
            })
        }
    }

    public forgetSelectedDevice() {
        let cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;
        // if (mediaDevice.kind === 'audioinput') {
        cache = {...cache, inputDeviceConfig: null};
        // }

        localStorage.setItem('preSelectedConfig', JSON.stringify(cache));

        this.inputDevices = this.inputDevices.map(d => {
            d.preselected = false;
            return d;
        });
    }

    public forgetSelectedOutputDevice(panelId: string) {
        let cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

        if (panelId.startsWith('bipanel_')) {
            const biPanelOutputDevices = cache.biPanelOutputDevicesConfig || {};
            delete biPanelOutputDevices[panelId];
            cache = {...cache, biPanelOutputDevicesConfig: biPanelOutputDevices};
        } else {
            const preSelectedOutputDevices = cache.outputDevicesConfig || {};
            delete preSelectedOutputDevices[panelId];
            cache = {...cache, outputDevicesConfig: preSelectedOutputDevices};
        }
        localStorage.setItem('preSelectedConfig', JSON.stringify(cache));

        this.outputDevices = this.outputDevices.map(d => {
            d.preselected = false;
            return d;
        });
    }

    private getRememberedDeviceFromStorage():
        { inputDevice: MediaDeviceInfoWithState | null, outputDevices: MediaDeviceInfoWithState[] } {

        const cache = JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config;

        const inputDevicesConfig = cache.inputDeviceConfig || null;
        const outputDevicesConfig = cache.outputDevicesConfig || {};
        const biPanelOutputDevicesConfig = cache.biPanelOutputDevicesConfig || {};

        if (inputDevicesConfig) {
            inputDevicesConfig.preselected = true;
        }

        let justOutputDevices: MediaDeviceInfoWithState[] = [];
        const allOutputConfigs = {...outputDevicesConfig, ...biPanelOutputDevicesConfig};
        for (const key in allOutputConfigs) {
            allOutputConfigs[key].preselected = true;
            allOutputConfigs[key].lastPanelId = key;
            justOutputDevices.push(allOutputConfigs[key]);
        }

        return {
            outputDevices: justOutputDevices,
            inputDevice: inputDevicesConfig
        };
    }

    public async enumerateDevices() {
        // devicechange can fire multiple times in quick succession (and polling can overlap),
        // which may produce duplicated disconnect/reconnect notifications.
        // Keep only one enumerate run in flight and coalesce extra requests.
        if (this.enumerateDevicesInFlight) {
            this.enumerateDevicesQueued = true;
            return this.enumerateDevicesInFlight;
        }

        this.enumerateDevicesInFlight = this.enumerateDevicesInternal()
            .finally(() => {
                this.enumerateDevicesInFlight = null;
                if (this.enumerateDevicesQueued) {
                    this.enumerateDevicesQueued = false;
                    void this.enumerateDevices();
                }
            });

        return this.enumerateDevicesInFlight;
    }

    private async enumerateDevicesInternal() {
        console.log("Enumerating devices... havePermissions ", this._havePermissions);

        if (!this._havePermissions) {
            await this.promptPermissions()
                .then(_p => {
                    console.debug("promptPermissions...", this._havePermissions);
                    this._havePermissions = true;
                })
                .catch((e) => {
                    console.error("promptPermissions...", e);

                    // @ts-ignore
                    if (e?.name !== "NotFoundError")
                        throw e;
                });
        }

        return await navigator.mediaDevices.enumerateDevices()
            .then(apiDevices => {
                let newInputDevices: MediaDeviceInfoWithState[] = [];
                let newOutputDevices: MediaDeviceInfoWithState[] = [];

                // Store references to the current device lists for comparison
                const oldInputDevices = this.inputDevices.map(d => this.copyDevice(d));
                const oldOutputDevices = this.outputDevices.map(d => this.copyDevice(d));

                const cachedDevices = this.getRememberedDeviceFromStorage();
                const cachedInputDevice = cachedDevices.inputDevice;
                const cachedOutputDevices = cachedDevices.outputDevices;

                const validDevices = apiDevices
                    .filter(d => d.kind === 'audioinput' || d.kind === 'audiooutput')
                    .filter(d => d.deviceId !== '')
                    .filter(d => d.deviceId !== 'default');

                const normalize = (value?: string) => (value || '').trim().toLowerCase();

                // Fuzzy-match a cached device against the current API device list by:
                // 1. exact deviceId  2. unique groupId (+ label tie-break)  3. unique label
                // This handles Edge rotating deviceIds across page reloads.
                const resolveCachedCandidate = (
                    kind: 'audioinput' | 'audiooutput',
                    cached: MediaDeviceInfoWithState | null,
                    devices: MediaDeviceInfo[]
                ): MediaDeviceInfo | null => {
                    if (!cached) return null;
                    const pool = devices.filter(d => d.kind === kind);
                    const byId = pool.find(d => d.deviceId === cached.deviceId);
                    if (byId) return byId;

                    if (cached.groupId) {
                        const byGroup = pool.filter(d => d.groupId && d.groupId === cached.groupId);
                        if (byGroup.length === 1) return byGroup[0];
                        if (byGroup.length > 1 && cached.label) {
                            const byGroupAndLabel = byGroup.filter(d => normalize(d.label) === normalize(cached.label));
                            if (byGroupAndLabel.length === 1) return byGroupAndLabel[0];
                        }
                    }

                    if (cached.label) {
                        const byLabel = pool.filter(d => normalize(d.label) === normalize(cached.label));
                        if (byLabel.length === 1) return byLabel[0];
                    }
                    return null;
                };

                // console.log("[0] apiDevices ", apiDevices);
                // console.log("[1] validDevices ", validDevices, cachedInputDevice, cachedOutputDevices);

                // Put API devices
                let found = false;
                let foundOutputs: string[] = []

                validDevices.forEach(d => {
                    if ('audioinput' === d.kind) {
                        let mediaDeviceInfoWithState = new MediaDeviceInfoWithState(d);
                        if (cachedInputDevice && d.deviceId === cachedInputDevice.deviceId) {
                            found = true;
                            // mediaDeviceInfoWithState.lastSeen = Date.now();
                            // mediaDeviceInfoWithState.preselected = true;
                            this.rememberSelectedDevice(mediaDeviceInfoWithState, false);
                        }

                        newInputDevices.push(mediaDeviceInfoWithState);
                    } else if ('audiooutput' === d.kind) {
                        const preselectedDevice = cachedOutputDevices.find(c => c.deviceId === d.deviceId);
                        let mediaOutputDeviceInfoWithState = new MediaDeviceInfoWithState(d);
                        // console.log('--> found', preselectedDevice, mediaOutputDeviceInfoWithState)
                        if (preselectedDevice) {
                            foundOutputs.push(mediaOutputDeviceInfoWithState.deviceId);
                            mediaOutputDeviceInfoWithState.lastSeen = Date.now();
                            // mediaOutputDeviceInfoWithState.preselected = preselectedDevice.preselected;
                            // mediaOutputDeviceInfoWithState.lastPanelId = preselectedDevice.lastPanelId;
                            this.rememberSelectedOutputDevice(mediaOutputDeviceInfoWithState, preselectedDevice.lastPanelId, false);
                        }

                        newOutputDevices.push(mediaOutputDeviceInfoWithState);
                    }
                })

                // Edge can return new deviceIds after reconnect. If a remembered input/output
                // has a unique stable match (groupId/label), update cache to the active device.
                const inputFallback = resolveCachedCandidate('audioinput', cachedInputDevice, validDevices);
                if (!found && cachedInputDevice && inputFallback) {
                    found = true;
                    this.rememberSelectedDevice(new MediaDeviceInfoWithState(inputFallback), false);
                }

                if (cachedOutputDevices.length > 0) {
                    cachedOutputDevices.forEach(cached => {
                        if (foundOutputs.includes(cached.deviceId)) return;
                        const fallback = resolveCachedCandidate('audiooutput', cached, validDevices);
                        if (!fallback) return;

                        foundOutputs.push(fallback.deviceId);
                        this.rememberSelectedOutputDevice(new MediaDeviceInfoWithState(fallback), cached.lastPanelId, false);
                    });
                }

                // === DISCONNECTED INPUT DEVICE PERSISTENCE ===
                // If cached input device was NOT found in current API devices, keep it visible
                // as "inactive" so user can see what was previously configured.
                // Device is forgotten after 10 days of being disconnected.
                if (!found && cachedInputDevice) {
                    if ((Date.now() - cachedInputDevice.lastSeen) > MediaDevicesUtils.TEN_DAYS) {
                        this.forgetSelectedDevice();
                    } else {
                        cachedInputDevice.isActive = false; // Mark as disconnected
                        newInputDevices.push(cachedInputDevice); // Keep in list for visibility
                    }
                }

                // === DISCONNECTED OUTPUT DEVICES PERSISTENCE ===
                // Same logic for output devices - keep disconnected devices visible
                // so users know which device was configured for each panel.
                if (cachedOutputDevices.length > 0) {
                    cachedOutputDevices.forEach(d => {
                        if (!foundOutputs?.includes(d.deviceId)) {
                            if ((Date.now() - d.lastSeen) > MediaDevicesUtils.TEN_DAYS) {
                                this.forgetSelectedOutputDevice(d.lastPanelId);
                            } else {
                                d.isActive = false; // Mark as disconnected
                                newOutputDevices.push(d); // Keep in list for visibility
                            }
                        }
                    })
                }

                // Avoid duplicate output rows when multiple cache entries reference the same
                // endpoint. Keep one row per exact deviceId; prefer active entries.
                const dedupedOutputById = new Map<string, MediaDeviceInfoWithState>();
                newOutputDevices.forEach(device => {
                    const existing = dedupedOutputById.get(device.deviceId);
                    if (!existing) {
                        dedupedOutputById.set(device.deviceId, device);
                        return;
                    }
                    if (!existing.isActive && device.isActive) {
                        dedupedOutputById.set(device.deviceId, device);
                    }
                });
                newOutputDevices = Array.from(dedupedOutputById.values());

                // Compare old devices vs new devices and find the changes
                const changes = {
                    newDevices: [] as string[], // new devices
                    reconnectedDevices: [] as string[], // devices that we've already seen but now reconnected
                    disconnectedDevices: [] as string[] // devices that got disconnected
                };

                // Check for new input devices or devices with changed isActive status
                // console.log("newInputDevices 3", newInputDevices);
                newInputDevices.forEach(newDevice => {
                    const oldDevice = oldInputDevices.find(d => d.deviceId === newDevice.deviceId) as MediaDeviceInfoWithState | undefined;
                    let newDeviceId = newDevice.deviceId;
                    if (!oldDevice) {
                        // This is a new device
                        changes.newDevices.push(newDeviceId);
                    } else if (oldDevice.isActive && !newDevice.isActive) {
                        // This device changed to inactive
                        changes.disconnectedDevices.push(newDeviceId);
                    } else if (!oldDevice.isActive && newDevice.isActive) {
                        // This device changed to active
                        changes.reconnectedDevices.push(newDeviceId);
                    }
                });

                oldInputDevices
                    .filter(oldD => !newInputDevices.find(newD => newD.deviceId === oldD.deviceId))
                    .forEach(oldD => {
                        oldD.isActive = false
                        // newInputDevices.push(oldD)
                        changes.disconnectedDevices.push(oldD.deviceId);
                    })

                // console.log("Check CHANGES - newOutputDevices", newOutputDevices)
                // console.log("Check CHANGES - oldOutputDevices", oldOutputDevices)

                // Check for new output devices or devices with changed isActive status
                newOutputDevices.forEach(newDevice => {
                    const oldDevice = oldOutputDevices.find(d => d.deviceId === newDevice.deviceId) as MediaDeviceInfoWithState | undefined;
                    let newDeviceId = newDevice.deviceId;

                    // console.log("Check CHANGES ", newDevice, oldDevice); //FIXME - aca queda raro, el output device queda como newDevices en lugar de reconnectedDevices
                    if (!oldDevice) {
                        // This is a new device
                        changes.newDevices.push(newDeviceId);
                    } else if (oldDevice.isActive && !newDevice.isActive) {
                        // This device changed to inactive
                        changes.disconnectedDevices.push(newDeviceId);
                    } else if (!oldDevice.isActive && newDevice.isActive) {
                        // This device changed its active
                        changes.reconnectedDevices.push(newDeviceId);
                    }
                });

                // Report outputs that disappeared entirely from enumeration.
                // Inputs already have the equivalent guard above; without this,
                // an uncached/stale output removal produces no device-change
                // broadcast, so Dashboard TTS can fall back to Windows default.
                oldOutputDevices
                    .filter(oldD => !newOutputDevices.find(newD => newD.deviceId === oldD.deviceId))
                    .forEach(oldD => {
                        changes.disconnectedDevices.push(oldD.deviceId);
                    });

                // Update the device lists
                this.inputDevices = newInputDevices;
                this.outputDevices = newOutputDevices;

                // console.log("Enumerated input devices: ", this.inputDevices, "Enumerated output devices: ", this.outputDevices);
                // console.log("Device changes: ", changes);

                if (changes.disconnectedDevices.length > 0) {
                    changes.disconnectedDevices.forEach(deviceId => this.freeAudioHandler(deviceId));
                }

                changes.newDevices = Array.from(new Set(changes.newDevices));
                changes.reconnectedDevices = Array.from(new Set(changes.reconnectedDevices));
                changes.disconnectedDevices = Array.from(new Set(changes.disconnectedDevices));

                const haveChanges = (changes.disconnectedDevices.length > 0) || (changes.newDevices.length > 0) || (changes.reconnectedDevices.length > 0);
                if (haveChanges) {
                    window.postMessage({
                        type: 'mediadevices-changed',
                        changes: changes
                    }, '*');
                }
                return validDevices.length > 0;
            })
            .catch(e => {
                console.error(e);
                throw e;
            })
    }

    private creatingByDevice: { [k: string]: Promise<MediaStream> } = {};
    private creatingAecByDevice: { [k: string]: boolean } = {};

    // Chrome tears down the physical device stream asynchronously in its audio service;
    // this pause between "free mismatched capture" and "re-acquire" lets that finish so
    // the new capture cannot rejoin the old device session and inherit its processing.
    // NOTE: the pause alone did NOT fix the post-Floor-Feed feedback loop (2000 ms was
    // tried and failed on the target Chrome/Windows and Edge) — the actual fix is
    // rebuilding the TTS playback AudioContexts after the new capture exists, so the
    // fresh capture's echo canceller receives their playout as reference (see
    // SessionContext.applyEchoCancellation / AudioPlayer.resetOutputContext). The pause
    // is kept as cheap insurance, and every capture is still logged with the applied
    // AEC value; the getSettings() retry below covers the cases Chrome reports honestly.
    private static readonly AEC_DISABLE_TEARDOWN_DELAY_MS = 400;
    private static readonly AEC_ENABLE_TEARDOWN_DELAY_MS = 500;
    private static readonly AEC_SWITCH_MAX_ATTEMPTS = 3;

    /**
     * Returns (creating if needed) the shared processed stream for an input device.
     *
     * `echoCancellation` is fixed at getUserMedia time — never reconfigured on a live track
     * (a runtime `applyConstraints` toggle was tried and reverted — glitchy and
     * browser-dependent). Instead, requesting a stream with the opposite setting frees the
     * cached capture and re-acquires it; the session contexts use this as the AEC toggle
     * mechanism (see `SessionContext.setEchoCancellation()`). Dashboard sessions always use
     * `true`. Two-Way sessions use `true` except while the Floor Feed is running, which
     * clones this shared capture via `getRawInputStreamClone()` instead of opening its own.
     * Passive consumers such as VU meters omit the flag: they reuse an existing stream, or
     * create an AEC-enabled stream when they are the first consumer.
     *
     * When an AEC switch frees a live capture, the re-acquisition waits a
     * direction-specific teardown pause (`AEC_ENABLE_TEARDOWN_DELAY_MS` /
     * `AEC_DISABLE_TEARDOWN_DELAY_MS`) so Chrome's audio service fully closes the old
     * device session — a quick re-open rejoins it and inherits the old processing while
     * getSettings() reports the requested value. The getSettings() verification retry
     * remains as a safety net for the cases Chrome does report honestly.
     */
    public getInputDeviceStream(deviceId: string, gain: number, echoCancellation?: boolean): Promise<MediaStream> {

        // Set when a live capture is freed because its AEC setting mismatches the request:
        // the case where Chrome's device pipeline is still warm and needs teardown time.
        let aecSwitchInProgress = false;

        const reuse = () => {
            const handler = this.intervenedInputStreams[deviceId];
            if (handler) {
                const ms = handler.getAudioStream();
                const isLive = ms.getAudioTracks().some(t => t.readyState === 'live');
                const aecMatches = echoCancellation === undefined
                    || handler.getEchoCancellation() === echoCancellation;
                if (isLive && aecMatches) {
                    // Ensure gain changes get applied when reusing existing handler
                    handler.setVolume(gain);
                    return Promise.resolve(ms);
                }
                // If not live (or captured with the wrong AEC setting), free it before recreating
                aecSwitchInProgress = isLive && !aecMatches;
                this.freeAudioHandler(deviceId);
            }
            return null;
        }

        const reused = reuse();
        if (reused) return reused;

        const inFlight = this.creatingByDevice[deviceId];
        if (inFlight) {
            if (echoCancellation === undefined || this.creatingAecByDevice[deviceId] === echoCancellation) {
                return inFlight;
            }
            // A creation with the opposite AEC setting is in flight: wait for it to settle,
            // drop it, and recreate with the requested setting.
            return inFlight
                .catch(() => null)
                .then(() => {
                    this.freeAudioHandler(deviceId);
                    return this.getInputDeviceStream(deviceId, gain, echoCancellation);
                });
        }

        const aec = echoCancellation ?? true;
        // Re-enabling AEC needs the long pause (warm rejoin silently keeps AEC off);
        // disabling gets by with the short one (honored on target hardware).
        const teardownDelayMs = aec
            ? MediaDevicesUtils.AEC_ENABLE_TEARDOWN_DELAY_MS
            : MediaDevicesUtils.AEC_DISABLE_TEARDOWN_DELAY_MS;
        const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

        const acquire = (attempt: number): Promise<MediaStream> =>
            navigator.mediaDevices.getUserMedia({
                // Keep AGC/noise suppression off to avoid browser VAD/noise gating artifacts.
                audio: {
                    deviceId: { exact: deviceId },
                    autoGainControl: false,
                    noiseSuppression: false,
                    echoCancellation: aec,
                }
            }).then(stream => {
                // `echoCancellation: true` is an ideal (best-effort) constraint, so a granted
                // capture is not proof AEC engaged. getSettings() reports what Chrome applied.
                const appliedAec = stream.getAudioTracks()[0]?.getSettings?.().echoCancellation;
                const coalesced = aecSwitchInProgress && appliedAec !== undefined && appliedAec !== aec;
                if (coalesced && attempt < MediaDevicesUtils.AEC_SWITCH_MAX_ATTEMPTS) {
                    // Chrome reused the old pipeline. Release this capture, give the device
                    // stream more time to die, and try again.
                    try {
                        stream.getTracks().forEach(track => track.stop());
                    } catch {
                    }
                    return wait(teardownDelayMs * attempt)
                        .then(() => acquire(attempt + 1));
                }
                if (coalesced) {
                    console.warn(`[MediaDevicesUtils] Chrome did not honor echoCancellation=${aec} for ${deviceId} after ${attempt} attempts — continuing with applied AEC: ${appliedAec}`);
                }
                return stream;
            });

        this.creatingAecByDevice[deviceId] = aec;
        this.creatingByDevice[deviceId] =
            (aecSwitchInProgress ? wait(teardownDelayMs) : Promise.resolve())
                .then(() => acquire(1))
                .then(stream => {
                    const newHandler = new AudioInputHandler(deviceId, gain, stream, aec);
                    this.intervenedInputStreams[deviceId] = newHandler;

                    const ms = newHandler.getAudioStream();
                    const isLive = ms.getAudioTracks().some(t => t.readyState === 'live');
                    if (isLive) {
                        // Ensure gain changes get applied
                        newHandler.setVolume(gain);
                    }
                    return ms;
                }).catch(err => {
                    console.error('[MediaDevicesUtils] getUserMedia failed for', deviceId, err.name, err.message);
                    throw err;

                }).finally(() => {
                    delete this.creatingByDevice[deviceId];
                    delete this.creatingAecByDevice[deviceId];
                });

        return this.creatingByDevice[deviceId];

    }

    /**
     * Returns a clone of the raw (pre-gain) microphone capture for `deviceId`, creating
     * the shared capture if none exists yet.
     *
     * Used by the Two-Way Floor Feed. Opening a second getUserMedia with
     * `echoCancellation: false` on the same physical microphone makes Chrome drop AEC on
     * the device's shared processing pipeline — silently disabling AEC on the recognition
     * stream, and AEC does not recover when the second capture closes. Cloning the single
     * shared capture avoids the conflicting constraints entirely.
     *
     * An existing live capture is cloned as-is; `echoCancellation` only applies when no
     * capture exists yet and one must be created (Floor Feed starting first). Two-Way
     * switches the shared capture to AEC-off via `SessionContext.setEchoCancellation(false)`
     * before starting the feed, so at clone time the capture carries the right setting.
     *
     * The clone is caller-owned: stopping its track never affects the shared capture or
     * recognition. Cloning the raw stream (not the post-gain output of AudioInputHandler)
     * keeps the passthrough at one AudioContext hop and at unity gain, so callers apply
     * their own gain exactly as before.
     */
    public getRawInputStreamClone(deviceId: string, echoCancellation?: boolean): Promise<MediaStream> {
        const cloneFromHandler = (): MediaStream | null => {
            const handler = this.intervenedInputStreams[deviceId];
            const rawTrack = handler?.getRawStream().getAudioTracks()
                .find(track => track.readyState === 'live');
            return rawTrack ? new MediaStream([rawTrack.clone()]) : null;
        };

        const existingClone = cloneFromHandler();
        if (existingClone) return Promise.resolve(existingClone);

        // No live shared capture yet (Floor Feed starting first, or the mic was freed):
        // create the shared capture and clone from it. The gain passed here only seeds
        // the handler; recognition applies its own gain on reuse.
        return this.getInputDeviceStream(deviceId, 1, echoCancellation).then(() => {
            const createdClone = cloneFromHandler();
            if (!createdClone) {
                throw new Error('Microphone capture is unavailable.');
            }
            return createdClone;
        });
    }

    public getAudioInputHandler(deviceId: string): AudioInputHandler {
        return this.intervenedInputStreams[deviceId];
    }

    public freeAllAudioInputHandlers() {
        Object.keys(this.intervenedInputStreams).forEach(deviceId => {
            try {
                this.intervenedInputStreams[deviceId]?.stop();
            } catch {
            }
            delete this.intervenedInputStreams[deviceId];
        });
    }

    public freeAudioHandler(deviceId: string) {
        try {
            this.intervenedInputStreams[deviceId]?.stop();
        } catch {
        }
        delete this.intervenedInputStreams[deviceId];
    }

    public getDevices(kind?: MediaDeviceKind): MediaDeviceInfoWithState[] {
        if ('audioinput' === kind) {
            return this.inputDevices;
        } else if ('audiooutput' === kind) {
            return this.outputDevices;
        }

        return [...this.inputDevices, ...this.outputDevices];
    }

    supportsDeviceChangeEvent(): boolean {
        return typeof navigator.mediaDevices?.ondevicechange !== 'undefined'
            && typeof navigator.mediaDevices?.addEventListener !== 'undefined';
    }


    public copyDevice(d: MediaDeviceInfoWithState): MediaDeviceInfoWithState {
        const clone = new MediaDeviceInfoWithState({
            deviceId: d.deviceId,
            groupId: d.groupId,
            kind: d.kind,
            label: d.label,
        } as MediaDeviceInfo);

        // copy mutable state
        clone.isActive = d.isActive;
        clone.preselected = d.preselected;
        clone.lastPanelId = d.lastPanelId;
        clone.lastSeen = d.lastSeen;

        return clone;
    }

}

export default MediaDevicesUtils;
